import { useState, useEffect } from "react";
import { BookOpen, Clock, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryColors = {
  "Maintenance": "bg-blue-500/20 text-blue-400",
  "Education": "bg-green-500/20 text-green-400",
  "Trends": "bg-purple-500/20 text-purple-400",
  "Troubleshooting": "bg-orange-500/20 text-orange-400",
};

export default function BlogSection() {
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${API_URL}/blog`);
        setPosts(response.data.filter(post => post.category !== "Education"));
      } catch {
        toast.error("Unable to load blog posts.");
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (selectedPost) {
    return (
      <section id="blog" className="py-24 bg-[#141414]" data-testid="blog-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => setSelectedPost(null)}
            className="mb-8 text-[#D4AF37] hover:text-[#F5D061] flex items-center gap-2 transition-colors"
            data-testid="blog-back-button"
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
            Back to all articles
          </button>

          <article
            className="bg-[#1A1A1A] border border-[#27272A] rounded-2xl overflow-hidden"
          >
            {selectedPost.image_url && (
              <img
                loading="lazy"
                src={selectedPost.image_url}
                alt={selectedPost.title}
                className="w-full h-64 object-cover"
              />
            )}
            <div className="p-8">
              <div className="flex items-center gap-4 mb-4">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${categoryColors[selectedPost.category] || "bg-gray-500/20 text-gray-400"}`}>
                  {selectedPost.category}
                </span>
                <span className="text-[#A1A1AA] text-sm flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {formatDate(selectedPost.created_at)}
                </span>
              </div>

              <h1 className="font-heading text-3xl text-white mb-4" data-testid="blog-post-title">
                {selectedPost.title}
              </h1>

              <p className="text-[#A1A1AA] text-sm mb-8">
                By {selectedPost.author}
              </p>

              <div 
                className="prose prose-invert prose-gold max-w-none"
                data-testid="blog-post-content"
              >
                {selectedPost.content.split('\n').map((paragraph, index) => {
                  const key = `p-${index}`;
                  if (paragraph.startsWith('## ')) {
                    return <h2 key={key} className="font-heading text-2xl text-white mt-8 mb-4">{paragraph.replace('## ', '')}</h2>;
                  } else if (paragraph.startsWith('### ')) {
                    return <h3 key={key} className="font-heading text-xl text-white mt-6 mb-3">{paragraph.replace('### ', '')}</h3>;
                  } else if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                    return <p key={key} className="text-white font-semibold my-4">{paragraph.replace(/\*\*/g, '')}</p>;
                  } else if (paragraph.startsWith('- ') || paragraph.startsWith('* ')) {
                    return <li key={key} className="text-[#A1A1AA] ml-4">{paragraph.replace(/^[-*] /, '')}</li>;
                  } else if (paragraph.match(/^\d+\./)) {
                    return <li key={key} className="text-[#A1A1AA] ml-4 list-decimal">{paragraph.replace(/^\d+\.\s*/, '')}</li>;
                  } else if (paragraph.trim() === '') {
                    return <br key={key} />;
                  } else {
                    return <p key={key} className="text-[#A1A1AA] my-4 leading-relaxed">{paragraph}</p>;
                  }
                })}
              </div>
            </div>
          </article>
        </div>
      </section>
    );
  }

  return (
    <section id="blog" className="py-24 bg-[#141414]" data-testid="blog-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div
          className="text-center mb-16"
        >
          <p className="overline mb-4" data-testid="blog-overline">Industry Insights</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mb-4" data-testid="blog-heading">
            The <span className="gold-text">Tibs Taps Blog</span>
          </h2>
          <p className="text-[#A1A1AA] max-w-2xl mx-auto text-lg">
            Expert tips, industry trends, and practical advice to help you get the most 
            from your draft beer system.
          </p>
        </div>

        {renderBlogContent()}
      </div>
    </section>
  );

  function renderBlogContent() {
    if (loading) {
      return (
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-[#1A1A1A] border border-[#27272A] rounded-xl p-6 animate-pulse">
              <div className="h-40 bg-[#27272A] rounded-lg mb-4" />
              <div className="h-4 bg-[#27272A] rounded w-1/4 mb-3" />
              <div className="h-6 bg-[#27272A] rounded mb-2" />
              <div className="h-4 bg-[#27272A] rounded w-3/4" />
            </div>
          ))}
        </div>
      );
    }

    if (posts.length === 0) {
      return (
        <div className="text-center py-12">
          <BookOpen className="w-12 h-12 text-[#A1A1AA] mx-auto mb-4" />
          <p className="text-[#A1A1AA]">Blog posts coming soon!</p>
        </div>
      );
    }

    return (
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {posts.map((post, index) => (
          <article
            key={post.id}
            className="bg-[#1A1A1A] border border-[#27272A] rounded-xl overflow-hidden card-hover cursor-pointer group"
            onClick={() => setSelectedPost(post)}
            data-testid={`blog-card-${index}`}
          >
            {post.image_url && (
              <div className="relative h-40 overflow-hidden">
                <img
                  loading="lazy"
                  src={post.image_url}
                  alt={post.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] to-transparent" />
              </div>
            )}
            <div className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${categoryColors[post.category] || "bg-gray-500/20 text-gray-400"}`}>
                  {post.category}
                </span>
              </div>
              <h3 className="font-medium text-white mb-2 line-clamp-2 group-hover:text-[#D4AF37] transition-colors">
                {post.title}
              </h3>
              <p className="text-[#A1A1AA] text-sm line-clamp-2 mb-4">
                {post.excerpt}
              </p>
              <div className="flex items-center justify-between">
                <span className="text-[#A1A1AA] text-xs">
                  {formatDate(post.created_at)}
                </span>
                <span className="text-[#D4AF37] text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                  Read
                  <ArrowRight className="w-3 h-3" />
                </span>
              </div>
            </div>
          </article>
        ))}
      </div>
    );
  }
}
